import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Info,
  Download,
  Link as LinkIcon,
  Trash2,
  MoreVertical,
  FileText,
  UploadCloud,
  Search,
  CheckCircle2,
  Image as ImageIcon,
  Files,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { UploadedFile } from '@terra/api-client';
import { Button, EmptyState, Input, Spinner } from '@terra/ui';

import { api } from '../lib/api';

function formatSize(bytes: number): string {
  if (bytes <= 0) return '—';
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  return `${(bytes / 1024).toFixed(2)} KB`;
}

const IMAGE_RE = /\.(png|jpe?g|gif|webp|svg|avif)$/i;

// ── File card ────────────────────────────────────────────────────────────────

function FileCard({
  file,
  checked,
  onCheck,
  onDelete,
  onDetails,
}: {
  file: UploadedFile;
  checked: boolean;
  onCheck: () => void;
  onDelete: () => void;
  onDetails: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const isImage = !!file.url && IMAGE_RE.test(file.name);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="border-border/60 bg-surface hover:border-gold-400 group relative flex flex-col overflow-visible rounded-2xl border shadow-sm transition-all hover:shadow-md"
    >
      <div className="relative">
        {/* Checkbox */}
        <label className="border-border/60 bg-surface/80 hover:bg-surface absolute left-2 top-2 z-10 grid h-6 w-6 cursor-pointer place-items-center rounded-lg border shadow-sm backdrop-blur-sm transition-colors">
          <input
            type="checkbox"
            checked={checked}
            onChange={onCheck}
            className="accent-gold-500 h-4 w-4 cursor-pointer rounded border-gray-300"
          />
        </label>

        {/* 3-dot menu */}
        <div ref={menuRef} className="absolute right-2 top-2 z-20">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="border-border/60 bg-surface/80 text-ink-muted hover:bg-surface hover:text-ink grid h-7 w-7 place-items-center rounded-lg border shadow-sm backdrop-blur-sm transition-colors"
            aria-label="File actions"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                className="border-border/60 bg-surface absolute right-0 top-9 z-[100] w-44 overflow-hidden rounded-xl border py-1 shadow-lg"
              >
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onDetails();
                  }}
                  className="text-ink-muted hover:bg-surface-subtle hover:text-ink flex w-full items-center gap-2.5 px-3 py-2 text-[12px] font-semibold transition-colors"
                >
                  <Info className="h-3.5 w-3.5" /> Details Info
                </button>
                <a
                  href={file.url ?? '#'}
                  target="_blank"
                  rel="noreferrer"
                  download
                  onClick={() => setMenuOpen(false)}
                  className={`hover:bg-surface-subtle flex w-full items-center gap-2.5 px-3 py-2 text-[12px] font-semibold transition-colors ${file.url ? 'text-ink-muted hover:text-ink' : 'pointer-events-none opacity-40'}`}
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </a>
                <button
                  type="button"
                  onClick={() => {
                    if (file.url) void navigator.clipboard.writeText(file.url);
                    setMenuOpen(false);
                  }}
                  className="text-ink-muted hover:bg-surface-subtle hover:text-ink flex w-full items-center gap-2.5 px-3 py-2 text-[12px] font-semibold transition-colors"
                >
                  <LinkIcon className="h-3.5 w-3.5" /> Copy Link
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete();
                  }}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-[12px] font-semibold text-red-600 transition-colors hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Thumbnail */}
        <div className="bg-surface-subtle/50 grid aspect-[4/3] place-items-center overflow-hidden rounded-t-2xl">
          {isImage ? (
            <img
              src={file.url!}
              alt={file.name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <FileText className="text-ink-subtle h-12 w-12 stroke-[1.5]" />
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-between px-4 py-3">
        <p className="text-ink truncate text-[13px] font-bold" title={file.name}>
          {file.name}
        </p>
        <p className="text-ink-muted mt-0.5 text-[11px] font-semibold">
          {formatSize(file.sizeBytes)}
        </p>
      </div>
    </motion.div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function UploadedFilesPage() {
  const qc = useQueryClient();
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'newest' | 'oldest' | 'name' | 'size'>('newest');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [details, setDetails] = useState<UploadedFile | null>(null);
  const [form, setForm] = useState({ name: '', url: '', sizeKb: '' });
  const [uploading, setUploading] = useState(false);

  const files = useQuery({
    queryKey: ['uploaded-files', search, sort, page],
    queryFn: () => api.files.list({ ...(search ? { q: search } : {}), sort, page }),
  });

  const register = useMutation({
    mutationFn: () =>
      api.files.register({
        name: form.name.trim(),
        url: form.url.trim(),
        ...(form.sizeKb ? { sizeBytes: Math.round(Number(form.sizeKb) * 1024) } : {}),
      }),
    onSuccess: () => {
      setForm({ name: '', url: '', sizeKb: '' });
      setShowUpload(false);
      void qc.invalidateQueries({ queryKey: ['uploaded-files'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.files.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['uploaded-files'] }),
  });
  const bulkDelete = useMutation({
    mutationFn: async (ids: string[]) => {
      return api.files.bulkDelete(ids);
    },
    onSuccess: (data) => {
      setSelected(new Set());
      setBulkAction('');
      if (data?.failed > 0) alert(`Bulk delete partial failure: ${data.failed} failed.`);
      void qc.invalidateQueries({ queryKey: ['uploaded-files'] });
    },
  });

  const rows = files.data?.data ?? [];
  const pageInfo = files.data?.page;
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="mb-2 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-ink text-[22px] font-extrabold tracking-tight">File Manager</h1>
          <p className="text-ink-muted mt-1 text-[13px] font-medium">
            Manage your uploaded assets, images, and documents.
          </p>
        </div>
        <button
          onClick={() => setShowUpload((v) => !v)}
          className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
        >
          {showUpload ? 'Close Uploader' : '+ Upload New File'}
        </button>
      </div>

      <AnimatePresence>
        {showUpload && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
          >
            <h3 className="text-ink-muted mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
              <UploadCloud className="h-4 w-4" /> Register New File
            </h3>
            <form
              className="grid items-end gap-5 sm:grid-cols-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (form.name && /^https?:\/\//.test(form.url)) register.mutate();
              }}
            >
              <div className="border-border/60 bg-surface-subtle/30 rounded-2xl border p-5 sm:col-span-3">
                <label className="text-ink mb-2 block text-[13px] font-bold">
                  Upload from device (Auto-fills details)
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    disabled={uploading}
                    className="text-ink-muted file:bg-ink hover:file:bg-ink/90 w-full max-w-sm text-[12px] transition-colors file:mr-4 file:cursor-pointer file:rounded-xl file:border-0 file:px-4 file:py-2.5 file:text-[12px] file:font-bold file:text-white disabled:opacity-50"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        setUploading(true);
                        // Get signature
                        const sig = await api.files.getUploadSignature({ folder: 'uploads' });
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('api_key', sig.apiKey);
                        formData.append('timestamp', String(sig.timestamp));
                        formData.append('signature', sig.signature);
                        formData.append('folder', sig.folder);

                        const res = await fetch(
                          `https://api.cloudinary.com/v1_1/${sig.cloudName}/auto/upload`,
                          {
                            method: 'POST',
                            body: formData,
                          },
                        );
                        if (!res.ok) throw new Error('Upload failed');
                        const data = await res.json();

                        setForm({
                          name: file.name,
                          url: data.secure_url,
                          sizeKb: (data.bytes / 1024).toFixed(2),
                        });
                      } catch (error) {
                        alert('Failed to upload file to Cloudinary.');
                        console.error(error);
                      } finally {
                        setUploading(false);
                      }
                    }}
                  />
                  {uploading && <Spinner size="md" />}
                </div>
              </div>

              <div>
                <Input
                  label="File Name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  hint="e.g. Logo.jpg"
                  required
                />
              </div>
              <div>
                <Input
                  label="Secure URL"
                  value={form.url}
                  onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                  hint="https://…"
                  required
                />
              </div>
              <div>
                <Input
                  label="Size (KB)"
                  type="number"
                  min="0"
                  value={form.sizeKb}
                  onChange={(e) => setForm((f) => ({ ...f, sizeKb: e.target.value }))}
                />
              </div>
              <div className="border-border/40 flex justify-end border-t pt-2 sm:col-span-3">
                <Button
                  type="submit"
                  loading={register.isPending}
                  disabled={uploading}
                  className="h-10 px-8"
                >
                  Save File Entry
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {/* Toolbar */}
        <div className="border-border/60 bg-surface-subtle/30 flex flex-col flex-wrap items-center justify-between gap-4 border-b px-5 py-4 sm:flex-row">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <Files className="h-4 w-4" strokeWidth={2} />
            </div>

            <div className="flex items-center gap-2">
              {selected.size > 0 && (
                <span className="text-ink-muted border-border/60 border-r px-2 text-[12px] font-bold">
                  {selected.size} selected
                </span>
              )}
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="border-border/60 bg-surface text-ink focus:border-gold-400 focus:ring-gold-400 h-9 rounded-xl border px-3 text-[12px] font-medium transition-colors focus:outline-none focus:ring-1"
              >
                <option value="">Bulk Action</option>
                <option value="DELETE">Delete files</option>
              </select>
              <button
                type="button"
                disabled={bulkAction !== 'DELETE' || selected.size === 0 || bulkDelete.isPending}
                onClick={() => {
                  if (window.confirm(`Delete ${selected.size} file(s)?`))
                    bulkDelete.mutate([...selected]);
                }}
                className="flex h-9 items-center justify-center rounded-xl border border-red-200 bg-red-50 px-4 text-[12px] font-bold text-red-600 shadow-sm transition-all hover:-translate-y-0.5 hover:bg-red-100 hover:shadow-md disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-sm"
              >
                Apply
              </button>
            </div>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as typeof sort);
                setPage(1);
              }}
              className="border-border/60 bg-surface text-ink focus:border-gold-400 focus:ring-gold-400 h-9 w-full rounded-xl border px-3 text-[12px] font-medium transition-colors focus:outline-none focus:ring-1 sm:w-auto"
            >
              <option value="newest">Sort: Newest First</option>
              <option value="oldest">Sort: Oldest First</option>
              <option value="name">Sort: A-Z (Name)</option>
              <option value="size">Sort: Size</option>
            </select>

            <div className="relative w-full sm:w-60">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="text-ink-muted h-4 w-4" strokeWidth={2} />
              </div>
              <input
                type="text"
                placeholder="Search files..."
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearch(searchDraft.trim());
                    setPage(1);
                  }
                }}
                className="border-border/60 bg-surface-subtle/50 text-ink focus:border-gold-400 focus:bg-surface focus:ring-gold-400 placeholder:text-ink-subtle h-9 w-full rounded-xl border pl-9 pr-3 text-[12px] font-medium transition-colors focus:outline-none focus:ring-1"
              />
            </div>
            <button
              onClick={() => {
                setSearch(searchDraft.trim());
                setPage(1);
              }}
              className="bg-ink hover:bg-ink/90 flex h-9 w-full items-center justify-center rounded-xl px-4 text-[12px] font-bold text-white shadow-sm transition-all sm:w-auto"
            >
              Search
            </button>
          </div>
        </div>

        {/* Gallery / Grid Area */}
        <div className="p-6">
          <label className="text-ink hover:text-gold-600 mb-4 flex w-fit cursor-pointer items-center gap-2 text-[12px] font-bold transition-colors">
            <input
              type="checkbox"
              checked={allChecked}
              onChange={() => setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.id)))}
              className="accent-gold-500 h-4 w-4 rounded border-gray-300"
            />
            Select All Files
          </label>

          {files.isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : rows.length === 0 ? (
            <div className="border-border/60 bg-surface-subtle/30 rounded-2xl border border-dashed py-12">
              <EmptyState
                icon={<Files className="text-ink-muted h-10 w-10" />}
                title="No files found"
                description="Upload images or documents to populate your file manager."
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              <AnimatePresence>
                {rows.map((file) => (
                  <FileCard
                    key={file.id}
                    file={file}
                    checked={selected.has(file.id)}
                    onCheck={() => toggle(file.id)}
                    onDelete={() => {
                      if (window.confirm(`Delete ${file.name}?`)) remove.mutate(file.id);
                    }}
                    onDetails={() => setDetails(file)}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}

          {/* Pagination */}
          {pageInfo && pageInfo.pageCount > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="border-border/60 bg-surface text-ink-muted hover:bg-surface-subtle flex h-9 w-9 items-center justify-center rounded-xl border transition-colors disabled:opacity-40"
              >
                &larr;
              </button>
              {Array.from({ length: pageInfo.pageCount }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPage(n)}
                  className={`flex h-9 w-9 items-center justify-center rounded-xl text-[12px] font-bold transition-colors ${
                    n === page
                      ? 'bg-gold-500 border-gold-500 border text-white shadow-sm'
                      : 'border-border/60 bg-surface text-ink-muted hover:bg-surface-subtle hover:text-ink border'
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                disabled={page >= pageInfo.pageCount}
                onClick={() => setPage((p) => Math.min(pageInfo.pageCount, p + 1))}
                className="border-border/60 bg-surface text-ink-muted hover:bg-surface-subtle flex h-9 w-9 items-center justify-center rounded-xl border transition-colors disabled:opacity-40"
              >
                &rarr;
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Details modal */}
      <AnimatePresence>
        {details && (
          <div
            className="bg-ink/40 fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={() => setDetails(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="border-border/60 bg-surface w-full max-w-md overflow-hidden rounded-2xl border shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-border/60 bg-surface-subtle/50 flex items-center justify-between border-b px-6 py-4">
                <h3 className="text-ink flex items-center gap-2 text-[16px] font-bold">
                  <Info className="text-gold-500 h-5 w-5" /> File Details
                </h3>
                <button
                  onClick={() => setDetails(null)}
                  className="text-ink-muted hover:text-ink transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6">
                <div className="bg-surface-subtle border-border/60 mb-5 flex aspect-video items-center justify-center overflow-hidden rounded-xl border shadow-inner">
                  {details.url && IMAGE_RE.test(details.name) ? (
                    <img
                      src={details.url}
                      alt={details.name}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <FileText className="text-ink-muted h-16 w-16 stroke-1" />
                  )}
                </div>

                <dl className="space-y-3 text-[13px]">
                  {[
                    ['Name', details.name],
                    ['Bucket', details.bucket],
                    ['Key', details.key],
                    ['Content type', details.contentType],
                    ['Size', formatSize(details.sizeBytes)],
                    ['Visibility', details.visibility],
                    ['Uploaded', new Date(details.createdAt).toLocaleString()],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4">
                      <dt className="text-ink-muted font-semibold">{k}</dt>
                      <dd className="text-ink truncate text-right font-bold">{v}</dd>
                    </div>
                  ))}
                  {details.url && (
                    <div className="border-border/40 mt-2 flex justify-between gap-4 border-t pt-2">
                      <dt className="text-ink-muted font-semibold">URL</dt>
                      <dd className="truncate text-right">
                        <a
                          href={details.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-gold-600 hover:text-gold-700 font-bold transition-colors"
                        >
                          {details.url}
                        </a>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
